import React, { useState } from 'react';
import Svg, { Path } from 'react-native-svg';

import { Box, Text } from 'app/shopify/theme';
import { IconButton } from 'app/shopify/components/actions/IconButton/IconButton';

import { Date as DateCell } from '../Date/Date';

export interface DatePickerProps {
  monthYear?: string;
}

interface CalendarDay {
  label: string;
}

const WEEKDAYS = [
  { id: 'sunday', label: 'S' },
  { id: 'monday', label: 'M' },
  { id: 'tuesday', label: 'T' },
  { id: 'wednesday', label: 'W' },
  { id: 'thursday', label: 'T' },
  { id: 'friday', label: 'F' },
  { id: 'saturday', label: 'S' },
] as const;

const WEEKS: CalendarDay[][] = [
  [{ label: '' }, { label: '' }, { label: '' }, { label: '' }, { label: '' }, { label: '1' }, { label: '2' }],
  [{ label: '3' }, { label: '4' }, { label: '5' }, { label: '6' }, { label: '7' }, { label: '8' }, { label: '9' }],
  [
    { label: '10' },
    { label: '11' },
    { label: '12' },
    { label: '13' },
    { label: '14' },
    { label: '16' },
    { label: '17' },
  ],
  [
    { label: '18' },
    { label: '19' },
    { label: '20' },
    { label: '21' },
    { label: '22' },
    { label: '23' },
    { label: '24' },
  ],
  [
    { label: '25' },
    { label: '26' },
    { label: '27' },
    { label: '28' },
    { label: '29' },
    { label: '30' },
    { label: '31' },
  ],
];

const PARSED_WEEKS = WEEKS.map((week) =>
  week.map((day) => ({
    ...day,
    value: day.label ? Number(day.label) : null,
  })),
);

export function DatePicker({ monthYear = 'Month 2026' }: DatePickerProps) {
  const [currentMonthDate, setCurrentMonthDate] = useState(() => {
    const initial = new Date();
    initial.setDate(1);
    return initial;
  });
  const [rangeStart, setRangeStart] = useState<number | null>(null);
  const [rangeEnd, setRangeEnd] = useState<number | null>(null);

  const selectDate = (dayValue: number) => {
    if (rangeStart === null) {
      setRangeStart(dayValue);
      setRangeEnd(null);
      return;
    }

    if (rangeEnd !== null) {
      setRangeStart(dayValue);
      setRangeEnd(null);
      return;
    }

    if (dayValue === rangeStart) {
      setRangeStart(null);
      setRangeEnd(null);
      return;
    }

    if (dayValue > rangeStart) {
      setRangeEnd(dayValue);
      return;
    }

    setRangeEnd(rangeStart);
    setRangeStart(dayValue);
  };

  const getDateVariant = (
    dayValue: number,
  ): { selected: boolean; type?: 'standalone' | 'start' | 'middle' | 'end' } => {
    if (rangeStart === null) return { selected: false };
    if (rangeEnd === null) {
      return {
        selected: dayValue === rangeStart,
        type: dayValue === rangeStart ? 'standalone' : undefined,
      };
    }

    const min = Math.min(rangeStart, rangeEnd);
    const max = Math.max(rangeStart, rangeEnd);
    if (dayValue < min || dayValue > max) return { selected: false };
    if (dayValue === min) return { selected: true, type: 'start' };
    if (dayValue === max) return { selected: true, type: 'end' };
    return { selected: true, type: 'middle' };
  };

  const onDayPress = (value: number | null) => {
    if (value === null) return;
    selectDate(value);
  };

  const monthLabel =
    monthYear !== 'Month 2026'
      ? monthYear
      : currentMonthDate.toLocaleDateString('en-US', {
          month: 'long',
          year: 'numeric',
        });

  const onPreviousMonth = () => {
    setCurrentMonthDate((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() - 1);
      return next;
    });
    setRangeStart(null);
    setRangeEnd(null);
  };

  const onNextMonth = () => {
    setCurrentMonthDate((prev) => {
      const next = new Date(prev);
      next.setMonth(next.getMonth() + 1);
      return next;
    });
    setRangeStart(null);
    setRangeEnd(null);
  };

  return (
    <Box width={370} gap="spacing.general.x3">
      <Box width="100%" flexDirection="row" alignItems="center" justifyContent="center" gap="spacing.general.x2">
        <IconButton variant="ghostNeutral" size="medium" icon={<ChevronLeftIcon />} onPress={onPreviousMonth} />

        <Text variant="bodyBaseMedium" style={{ flex: 1, textAlign: 'center' }}>
          {monthLabel}
        </Text>

        <IconButton variant="ghostNeutral" size="medium" icon={<ChevronRightIcon />} onPress={onNextMonth} />
      </Box>

      <Box width="100%">
        <Box width="100%" flexDirection="row" alignItems="center" justifyContent="center">
          {WEEKDAYS.map(({ id, label }) => (
            <Box key={id} flex={1} height={40} borderRadius="radius.full" alignItems="center" justifyContent="center">
              <Text variant="bodyBaseMedium" color="text.secondary">
                {label}
              </Text>
            </Box>
          ))}
        </Box>

        <Box width="100%" style={{ rowGap: 2 }}>
          {PARSED_WEEKS.map((week, weekIndex) => (
            <Box
              key={`week-${String(weekIndex)}`}
              width="100%"
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
            >
              {week.map((day, dayIndex) => (
                <Box
                  key={`day-${String(weekIndex)}-${String(dayIndex)}`}
                  flex={1}
                  alignItems="center"
                  justifyContent="center"
                >
                  {day.value !== null ? (
                    <DateRangeCell
                      label={day.label}
                      value={day.value}
                      getDateVariant={getDateVariant}
                      onPress={onDayPress}
                    />
                  ) : (
                    <DateCell state="null" />
                  )}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ChevronLeftIcon() {
  return (
    <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.5 7C15.5 6.8 15.44 6.61 15.33 6.44C15.22 6.28 15.07 6.15 14.88 6.08C14.7 6 14.5 5.98 14.3 6.02C14.11 6.06 13.93 6.15 13.79 6.29L8.79 11.29C8.61 11.48 8.5 11.73 8.5 12C8.5 12.27 8.61 12.52 8.79 12.71L13.79 17.71C13.93 17.85 14.11 17.94 14.3 17.98C14.5 18.02 14.7 18 14.88 17.92C15.07 17.85 15.22 17.72 15.33 17.56C15.44 17.39 15.5 17.2 15.5 17V7Z"
        fill="currentColor"
      />
    </Svg>
  );
}

function ChevronRightIcon() {
  return (
    <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
      <Path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5 17C8.5 17.2 8.56 17.39 8.67 17.56C8.78 17.72 8.93 17.85 9.12 17.92C9.3 18 9.5 18.02 9.7 17.98C9.89 17.94 10.07 17.85 10.21 17.71L15.21 12.71C15.39 12.52 15.5 12.27 15.5 12C15.5 11.73 15.39 11.48 15.21 11.29L10.21 6.29C10.07 6.15 9.89 6.06 9.7 6.02C9.5 5.98 9.3 6 9.12 6.08C8.93 6.15 8.78 6.28 8.67 6.44C8.56 6.61 8.5 6.8 8.5 7V17Z"
        fill="currentColor"
      />
    </Svg>
  );
}

function DateRangeCell({
  label,
  value,
  getDateVariant,
  onPress,
}: {
  label: string;
  value: number;
  getDateVariant: (dayValue: number) => { selected: boolean; type?: 'standalone' | 'start' | 'middle' | 'end' };
  onPress: (value: number | null) => void;
}) {
  const variant = getDateVariant(value);

  return (
    <DateCell
      digit={label}
      selected={variant.selected}
      type={variant.type}
      state="enabled"
      onPress={() => onPress(value)}
    />
  );
}
